/* =============================================================
   listicle-psp.js
   Behavior for the MTJ listicle PSP:
   - Reading progress bar
   - GA4 scroll milestone events (25/50/75)
   - Size-card selector (main offer + sticky side)
   - Desktop sticky side offer show/hide
   - Exit-intent modal (desktop mousemove; mobile inactivity)
   Scoped to the listicle template by presence of .listicle-7r.
   ============================================================= */
(function () {
  'use strict';

  if (!document.querySelector('.shopify-section--listicle-7r, .listicle-7r')) {
    return;
  }

  // ---------------------------------------------------------
  // GA4 scroll milestones (25/50/75 only; GA4 Enhanced
  // Measurement already fires 90%). Each milestone fires once
  // per page-load via in-memory flag set.
  // ---------------------------------------------------------
  var scrollMilestones = [25, 50, 75];
  var firedMilestones = Object.create(null);
  var ga4WarnedMissing = false;

  function fireGa4Scroll(depth) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('listicle_scroll', { percent_scrolled: depth, page: 'mtj_7_reasons' });
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'scroll', { percent_scrolled: depth });
        return;
      }
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'scroll', percent_scrolled: depth });
        return;
      }
      if (!ga4WarnedMissing) {
        ga4WarnedMissing = true;
        if (window.console && console.warn) {
          console.warn('[listicle-psp] GA4 unavailable: no gtag or dataLayer found; scroll milestones not sent.');
        }
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[listicle-psp] GA4 scroll event failed', err);
      }
    }
  }

  function checkMilestones(pct) {
    for (var i = 0; i < scrollMilestones.length; i++) {
      var m = scrollMilestones[i];
      if (!firedMilestones[m] && pct >= m) {
        firedMilestones[m] = true;
        fireGa4Scroll(m);
      }
    }
  }

  // ---------------------------------------------------------
  // Reading progress bar
  // ---------------------------------------------------------
  var progressBar = document.querySelector('.listicle-psp-progress__bar');
  function updateProgress() {
    var scrollTop = window.scrollY || document.documentElement.scrollTop;
    var docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight
    ) - window.innerHeight;
    var pct = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
    if (progressBar) {
      progressBar.style.width = pct + '%';
    }
    checkMilestones(pct);
  }
  updateProgress();
  window.addEventListener('scroll', updateProgress, { passive: true });
  window.addEventListener('resize', updateProgress);

  // ---------------------------------------------------------
  // Shared size-card selection state
  // Sources of truth: data-size on each card. All cards with the
  // same data-size stay in sync. Clicking a card updates the CTA
  // hrefs to the size-specific PDP URL.
  // ---------------------------------------------------------
  var SIZE_CLASSIC = 'classic';
  var SIZE_POCKET = 'pocket';
  var currentSize = SIZE_CLASSIC; // pre-selected default

  function urlForSize(size) {
    var card = document.querySelector(
      '.listicle-psp-offer__sizecard[data-size="' + size + '"], .listicle-psp-side__pill[data-size="' + size + '"]'
    );
    return card ? card.getAttribute('data-url') : null;
  }

  function syncCards() {
    var mainCards = document.querySelectorAll('.listicle-psp-offer__sizecard');
    mainCards.forEach(function (el) {
      var sel = el.getAttribute('data-size') === currentSize;
      el.classList.toggle('is-selected', sel);
      el.setAttribute('aria-checked', sel ? 'true' : 'false');
    });
    var pills = document.querySelectorAll('.listicle-psp-side__pill');
    pills.forEach(function (el) {
      var sel = el.getAttribute('data-size') === currentSize;
      el.classList.toggle('is-selected', sel);
      el.setAttribute('aria-checked', sel ? 'true' : 'false');
    });

    var newHref = urlForSize(currentSize);
    if (newHref) {
      var ctas = document.querySelectorAll(
        '.listicle-psp-offer__cta, .listicle-psp-side__cta'
      );
      ctas.forEach(function (a) { a.setAttribute('href', newHref); });
    }
  }

  function onSizeClick(e) {
    var t = e.currentTarget;
    var size = t.getAttribute('data-size');
    if (!size) return;
    currentSize = size;
    syncCards();
  }

  var allSizeEls = document.querySelectorAll(
    '.listicle-psp-offer__sizecard, .listicle-psp-side__pill'
  );
  allSizeEls.forEach(function (el) {
    el.addEventListener('click', onSizeClick);
  });
  syncCards();

  // ---------------------------------------------------------
  // Desktop sticky side offer
  // - Only appears once user scrolls > 300px
  // - Hides when main offer is in view (IntersectionObserver)
  // - CSS already gates display to >= 1024px
  // ---------------------------------------------------------
  var side = document.getElementById('listicle-psp-side');
  var mainOffer = document.getElementById('listicle-psp-offer');

  var mainOfferVisible = false;
  if ('IntersectionObserver' in window && mainOffer && side) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        mainOfferVisible = entry.isIntersecting;
        applySideVisibility();
      });
    }, { threshold: 0.08 });
    io.observe(mainOffer);
  }

  function applySideVisibility() {
    if (!side) return;
    var scrolled = (window.scrollY || document.documentElement.scrollTop) > 300;
    var desktop = window.matchMedia('(min-width: 1024px)').matches;
    var show = desktop && scrolled && !mainOfferVisible;
    side.classList.toggle('is-visible', show);
    side.setAttribute('aria-hidden', show ? 'false' : 'true');
  }
  window.addEventListener('scroll', applySideVisibility, { passive: true });
  window.addEventListener('resize', applySideVisibility);
  applySideVisibility();

  // ---------------------------------------------------------
  // Exit-intent modal
  // - Desktop: mousemove with clientY <= 10 near top of viewport
  // - Mobile: 20s inactivity AND scroll depth >= 25%
  // - Once per session via localStorage flag
  // ---------------------------------------------------------
  var modal = document.getElementById('listicle-psp-exit');
  var SHOWN_KEY = 'mtj_exit_intent_shown';

  function alreadyShown() {
    try { return localStorage.getItem(SHOWN_KEY) === '1'; } catch (e) { return false; }
  }
  function markShown() {
    try { localStorage.setItem(SHOWN_KEY, '1'); } catch (e) {}
  }

  function showExitModal() {
    if (!modal || alreadyShown()) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    markShown();
  }
  function hideExitModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
  }

  if (modal) {
    modal.querySelectorAll('[data-exit-dismiss]').forEach(function (el) {
      el.addEventListener('click', hideExitModal);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hideExitModal();
    });

    // Desktop mousemove trigger
    if (window.matchMedia('(min-width: 1024px)').matches) {
      document.addEventListener('mousemove', function (e) {
        if (alreadyShown()) return;
        if (e.clientY <= 10) {
          showExitModal();
        }
      });
    } else {
      // Mobile: inactivity AND scroll depth >= 25%
      var lastActivity = Date.now();
      var activityEvents = ['touchstart', 'scroll', 'mousemove', 'keydown'];
      activityEvents.forEach(function (ev) {
        window.addEventListener(ev, function () { lastActivity = Date.now(); }, { passive: true });
      });
      setInterval(function () {
        if (alreadyShown()) return;
        var idleMs = Date.now() - lastActivity;
        var scrollTop = window.scrollY || document.documentElement.scrollTop;
        var docHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight
        ) - window.innerHeight;
        var pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
        if (idleMs >= 20000 && pct >= 25) {
          showExitModal();
        }
      }, 2000);
    }
  }
})();
